import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Paper,
  Alert,
  InputAdornment,
  ListSubheader,
  IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid } from '@mui/x-data-grid';
import { FinancialDataAPI, FinancialGridRow } from '@features/ocr/types/types';

interface STRReportProps {
  financialData: FinancialDataAPI | null;
  gridRows: FinancialGridRow[];
  gridColumns: Array<{
    field: string;
    headerName?: string;
    [key: string]: unknown;
  }>;
  addFormCategory: number | '';
  addFormItemName: string;
  addFormYear: string;
  addFormAmount: string;
  saving: boolean;
  setAddFormCategory: (value: number | '') => void;
  setAddFormItemName: (value: string) => void;
  setAddFormYear: (value: string) => void;
  setAddFormAmount: (value: string) => void;
  handleAddNewItem: () => Promise<void>;
  handleDeleteClick: (row: FinancialGridRow) => void;
  processRowUpdate: (newRow: FinancialGridRow) => Promise<FinancialGridRow>;
  onProcessRowUpdateError: (error: Error) => void;
  loading: boolean;
  selectedHotel: string;
  setError: (value: string | null) => void;
}

const STRReport: React.FC<STRReportProps> = ({
  financialData,
  gridRows,
  gridColumns,
  addFormCategory,
  addFormItemName,
  addFormYear,
  addFormAmount,
  saving,
  setAddFormCategory,
  setAddFormItemName,
  setAddFormYear,
  setAddFormAmount,
  handleAddNewItem,
  processRowUpdate,
  onProcessRowUpdateError,
  loading,
  selectedHotel,
  setError
}) => {
  // State to track which years have been deleted
  const [stagedDeletions, setStagedDeletions] = useState<Set<string>>(new Set());
  const [hasPendingChanges, setHasPendingChanges] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Get all available years and filter out staged deletions
  const years = financialData?.years || [];
  const activeYears = years.filter(year => !stagedDeletions.has(year));

  // Stage a deletion instead of immediately deleting
  const handleStageDeleteYear = (year: string) => {
    if (window.confirm(`Are you sure you want to delete all STR Report data for ${year}?`)) {
      setStagedDeletions(prev => {
        const newSet = new Set(prev);
        newSet.add(year);
        return newSet;
      });
      setHasPendingChanges(true);
    }
  };

  // CSV Export
  const handleExportCSV = () => {
    if (!filteredRows.length) return;
    const csvRows = [];
    // Header
    csvRows.push(columnsWithActions.map((col) => col.headerName || col.field).join(','));
    // Rows
    filteredRows.forEach(row => {
      csvRows.push(columnsWithActions.map((col) => row[col.field] ?? '').join(','));
    });
    const csvContent = 'data:text/csv;charset=utf-8,' + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'str_report.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Save all pending changes
  const handleSaveChanges = async () => {
    if (!hasPendingChanges) return;
    setIsSaving(true);
    setError?.(null);
    try {
      // Process each staged deletion
      const results: Array<{ year: string, deletedCount: number }> = [];
      for (const year of stagedDeletions) {
        const response = await fetch(`/api/ocr/hotel/${selectedHotel}/str-report/year/${year}`, { method: 'DELETE' });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.details || 'Failed to delete data');
        }
        const result = await response.json();
        results.push({ year, deletedCount: result.updatedCount });
      }
      setStagedDeletions(new Set());
      setHasPendingChanges(false);
      const totalDeleted = results.reduce((sum, item) => sum + item.deletedCount, 0);
      setError?.(`Successfully deleted ${totalDeleted} STR Report records`);
    } catch (error) {
      setError?.(`Failed to save changes: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Custom toolbar with export button
  const CustomToolbar = () => (
    <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      p: 1
    }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', pl: 1 }}>
        STR Report Data
      </Typography>
      <Button
        startIcon={<DownloadIcon />}
        variant="outlined"
        color="primary"
        onClick={handleExportCSV}
        size="small"
        sx={{ borderRadius: 1.5 }}
      >
        EXPORT CSV
      </Button>
    </Box>
  );

  // Filter only relevant rows for STR Report
  const filteredRows = gridRows.filter(row => row.documentType === 'STR Report');

  // Filter out columns for deleted years
  const activeColumns = React.useMemo(() => gridColumns.filter(column => {
    // If it's a year column, check if it's in activeYears
    if (!isNaN(Number(column.field))) {
      return activeYears.includes(column.field);
    }
    // Keep non-year columns
    return true;
  }), [gridColumns, activeYears]);

  // Add delete icons to year columns
  const columnsWithActions = React.useMemo(() => activeColumns.map(column => {
    if (!isNaN(Number(column.field))) {
      return {
        ...column,
        headerName: `${column.headerName}`,
        renderHeader: () => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span>{column.headerName}</span>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleStageDeleteYear(column.field);
              }}
              sx={{ ml: 1, '&:hover': { color: 'error.main' } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      };
    }
    return column;
  }), [activeColumns, handleStageDeleteYear]);

  // DataGrid Footer: Customized for better horizontal scrolling
  const dataGridFooter = React.useCallback(() => (
    <Box sx={{
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      px: 2,
      py: 1.5,
      bgcolor: 'action.hover',
      borderTop: '1px solid',
      borderColor: 'divider',
      position: 'static',
      bottom: 0,
      zIndex: 10,
      width: '100%',
      minWidth: 'max-content',
      overflowX: 'auto'
    }}>
      <Typography variant="subtitle2" sx={{ mr: 2, fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap' }}>Averages:</Typography>
      {columnsWithActions.filter(col => !isNaN(Number(col.field))).map((col, idx) => {
        const total = filteredRows.reduce((sum, row) => sum + (parseFloat(String(row[col.field] ?? "")) || 0), 0);
        const average = filteredRows.length > 0 ? total / filteredRows.length : 0;
        return (
          <Typography key={col.field} variant="body2" sx={{
            minWidth: 80,
            textAlign: 'right',
            fontWeight: 600,
            color: average > 0 ? 'primary.main' : average < 0 ? 'error.main' : 'text.primary',
            mr: idx === columnsWithActions.length - 1 ? 0 : 2,
            whiteSpace: 'nowrap'
          }}>
            {average !== 0 ? (col.field.toLowerCase().includes('occupancy') ?
              `${average.toLocaleString(undefined, { maximumFractionDigits: 1 })}%` :
              `$${average.toLocaleString(undefined, { maximumFractionDigits: 0 })}`) : '-'}
          </Typography>
        );
      })}
    </Box>
  ), [columnsWithActions, filteredRows]);

  // Add a message if no data is found
  if (filteredRows.length === 0) {
    return (
      <>
        <Alert
          severity="info"
          sx={{
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}
          icon={<InfoIcon />}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              STR Report Guide
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
            STR (Smith Travel Research) data shows competitive market metrics. Key metrics include Occupancy Rate (%), Average Daily Rate (ADR), Revenue Per Available Room (RevPAR), and Market Penetration.
          </Typography>
        </Alert>

        <Paper
          sx={{
            p: 4,
            mb: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: '0 4px 8px rgba(0,0,0,0.08)',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="h5" gutterBottom color="text.primary" sx={{ fontWeight: 500 }}>
            No STR Report data found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '600px', mb: 3 }}>
            To add financial data for your STR Report, you can either upload an STR document
            using the document uploader or manually add items using the form below.
          </Typography>
          <Typography variant="subtitle1" sx={{ maxWidth: '600px', mb: 3, fontWeight: 500, bgcolor: 'action.hover', p: 2, borderRadius: 1, width: '100%' }}>
            Start by adding key performance metrics like Occupancy Rate, ADR (Average Daily Rate),
            RevPAR (Revenue Per Available Room), and Competitive Set data.
          </Typography>
        </Paper>
      </>
    );
  }

  return (
    <Box sx={{
      maxWidth: '100%',
      mx: 'auto',
      p: { xs: 1, md: 2 },
      width: '100%',
      height: 'auto',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'visible',
      '& *': {
        boxSizing: 'border-box'
      }
    }}>
      {/* Info Section */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2.5,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            STR Report Guide
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', lineHeight: 1.5 }}>
          Smith Travel Research (STR) reports provide hotel performance data compared to your competitive set.
          Key metrics include Occupancy (%), ADR (Average Daily Rate in $), RevPAR (Revenue Per Available Room in $),
          and Market Penetration (performance vs. competition, where {'>'} 100% is better than average).
        </Typography>
      </Paper>

      {/* Save/Cancel for column deletes */}
      {hasPendingChanges && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSaveChanges}
            disabled={isSaving}
            sx={{ mr: 2, height: 40, borderRadius: 1.5 }}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setStagedDeletions(new Set());
              setHasPendingChanges(false);
            }}
            disabled={isSaving}
            sx={{ height: 40, borderRadius: 1.5 }}
          >
            Cancel
          </Button>
        </Box>
      )}

      {/* Add New Item Form */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ mb: 2, fontSize: '1rem', fontWeight: 600, color: 'text.primary' }}>
          Add New STR Metric
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }} variant="outlined" size="small" required error={addFormCategory === ''}>
            <InputLabel id="add-category-label">Metric Type *</InputLabel>
            <Select
              labelId="add-category-label"
              label="Metric Type *"
              value={addFormCategory}
              onChange={(e) => setAddFormCategory(e.target.value as number)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: !addFormCategory ? 'error.main' : undefined,
                },
                height: 40,
                bgcolor: 'background.paper'
              }}
            >
              <MenuItem value="" disabled>
                <em>Select metric type</em>
              </MenuItem>
              {/* Render grouped options here */}
              {financialData && Object.entries(
                financialData.categories.reduce((groups: { [key: string]: Array<typeof financialData.categories[0]> }, category) => {
                  const group = category.category_group;
                  if (!groups[group]) groups[group] = [];
                  groups[group].push(category);
                  return groups;
                }, {}),
              ).map(([group, categories]) => [
                <ListSubheader key={group} sx={{ backgroundColor: 'background.paper', fontWeight: 600, color: 'text.primary' }}>{group}</ListSubheader>,
                ...categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                )),
              ])}
            </Select>
            {!addFormCategory && <Typography variant="caption" color="error.main" sx={{ mt: 0.5 }}>Required</Typography>}
          </FormControl>

          <TextField
            label="Metric Name *"
            value={addFormItemName}
            onChange={(e) => setAddFormItemName(e.target.value)}
            variant="outlined"
            size="small"
            required
            error={!addFormItemName.trim()}
            helperText={!addFormItemName.trim() ? 'Required' : ''}
            sx={{ minWidth: 180, '& .MuiInputBase-root': { height: 40 } }}
            placeholder="e.g., Occupancy Rate, RevPAR, ADR"
          />

          <TextField
            label="Year *"
            value={addFormYear}
            onChange={e => setAddFormYear(e.target.value)}
            size="small"
            required
            error={!addFormYear}
            helperText={!addFormYear ? 'Required' : ''}
            sx={{ width: 120, '& .MuiInputBase-root': { height: 40 } }}
          />

          <TextField
            label="Value *"
            value={addFormAmount}
            onChange={e => setAddFormAmount(e.target.value)}
            size="small"
            type="number"
            required
            error={!addFormAmount || isNaN(parseFloat(addFormAmount))}
            helperText={!addFormAmount ? 'Required' : isNaN(parseFloat(addFormAmount)) ? 'Invalid Number' : ''}
            InputProps={{
              endAdornment: addFormItemName.toLowerCase().includes('occupancy') ? (
                <InputAdornment position="end">%</InputAdornment>
              ) : (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            sx={{ width: 150, '& .MuiInputBase-root': { height: 40 } }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleAddNewItem}
            disabled={saving || !addFormCategory || !addFormItemName.trim() || !addFormYear || !addFormAmount || isNaN(parseFloat(addFormAmount))}
            startIcon={<AddIcon />}
            sx={{
              height: 40,
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' },
              borderRadius: 1.5,
              boxShadow: 1
            }}
          >
            {saving ? 'Adding...' : 'ADD METRIC'}
          </Button>
        </Box>
      </Paper>

      {/* DataGrid */}
      <Paper
        elevation={0}
        sx={{
          flexGrow: 1,
          height: 'auto',
          minHeight: 300,
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          overflow: 'visible',
          mb: 4,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          width: '100%',
          maxWidth: '100%',
          flex: '1 1 auto',
          position: 'relative'
        }}
      >
        {/* Wrap DataGrid in a div with scrolling */}
        <Box sx={{
          width: '100%',
          height: 'auto',
          maxHeight: '55vh',
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: '4px'
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.05)'
          }
        }}>
          <DataGrid
            rows={filteredRows}
            columns={columnsWithActions}
            loading={loading || saving || isSaving}
            editMode="row"
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={onProcessRowUpdateError}
            density="compact"
            disableColumnMenu
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            autoHeight
            hideFooterSelectedRowCount
            isCellEditable={(params) => {
              // Make itemName and all year fields editable
              return ['itemName'].includes(params.field) || !isNaN(Number(params.field));
            }}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 }
              },
              columns: {
                columnVisibilityModel: {},
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            slots={{
              toolbar: CustomToolbar,
              footer: dataGridFooter,
            }}
            sx={{
              width: '100%',
              border: 'none',
              '& .MuiDataGrid-root': {
                width: '100%',
                border: 'none'
              },
              '& .MuiDataGrid-main': {
                overflow: 'visible',
                width: '100%'
              },
              '& .MuiDataGrid-virtualScroller': {
                overflow: 'visible',
                maxHeight: '100% !important'
              },
              '& .MuiDataGrid-cell': {
                overflow: 'visible',
                textOverflow: 'clip',
                whiteSpace: 'nowrap',
                padding: '0 12px',
                lineHeight: '36px',
                fontSize: '0.875rem',
                color: 'text.primary',
                borderColor: 'rgba(224, 224, 224, 0.4)'
              },
              '& .MuiDataGrid-columnHeaders': {
                whiteSpace: 'normal',
                lineHeight: '1.2',
                backgroundColor: 'action.hover',
                borderColor: 'divider',
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 600,
                  color: 'text.primary'
                }
              },
              '& .MuiDataGrid-columnHeader': {
                borderColor: 'divider'
              },
              '& .MuiDataGrid-row': {
                minHeight: '36px',
                '&:nth-of-type(even)': {
                  backgroundColor: 'action.hover'
                },
                '&:hover': {
                  backgroundColor: 'action.selected'
                },
                '&:last-child': {
                  borderBottom: 'none'
                }
              },
              '& .MuiDataGrid-virtualScrollerContent': {
                minWidth: '100%'
              },
              '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
                '&:not(.MuiDataGrid-withBorderColor)': {
                  borderRight: `1px solid rgba(224, 224, 224, 0.4)`
                }
              },
              '& .MuiTablePagination-root': {
                marginTop: 'auto',
                marginBottom: 0,
                color: 'text.secondary'
              },
              '& .MuiDataGrid-toolbarContainer': {
                padding: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid',
                borderColor: 'divider'
              }
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default STRReport;